import { Edit as EditIcon, Notes as NotesIcon } from '@mui/icons-material'
import { Grid } from '@mui/material'
import { RichTextInput } from 'ra-input-rich-text'
import { useCallback, useRef, useState } from 'react'
import {
  Button,
  Datagrid,
  DateField,
  DeleteButton,
  FileInput,
  FormDataConsumer,
  FormTab,
  FunctionField,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  required,
  RichTextField,
  SelectInput,
  SimpleForm,
  TabbedForm,
  TextField,
  TextInput,
  useDataProvider,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
  AutocompleteTextInput,
  CreateModal,
  EditModal,
  Empty,
  FileImageField,
  HistoryDatagrid,
  TabToolbar,
} from '~/components'
import { customerName } from '~/helpers/resources/customers'

export const EditComments = (props = {}) => {
  // Fallback when no relation is passed: the page's resource and id.
  // (props.match was react-router 5's and no longer exists.)
  const pageResource = useResourceContext()
  const { id: pageId } = useParams()
  const { relation = [pageResource, pageId], stateCreate, disableDelete, listLinked } = props

  const stateEditModal = useState(false)
  const [editCommentId, edit] = useState(0)

  const refresh = useRefresh()
  // react-admin 4 removed the generic useMutation; call the dataProvider
  // directly for these ad-hoc writes.
  const dataProvider = useDataProvider()
  const mutate = ({ type, resource, payload }) => dataProvider[type](resource, payload)

  // react-admin 5's useRecordContext() is undefined until the record loads.
  const { boat_customer } = useRecordContext() ?? {}
  // TODO: Make this more generic (working for other entities than boats/customers)
  const linkedType =
    boat_customer && boat_customer.length > 0
      ? boat_customer[0].boat_id
        ? 'boats'
        : 'customers'
      : null
  const linkedSelectionFilter =
    linkedType === 'boats'
      ? {
          id: boat_customer.map((bc) => bc.boat_id),
        }
      : linkedType === 'customers'
        ? {
            id: boat_customer.map((bc) => bc.customer_id),
          }
        : {}
  const linkedOptionText = (record) => {
    return linkedType === 'boats'
      ? `${record.model} (${record.name})`
      : linkedType === 'customers'
        ? customerName(record)
        : ''
  }

  // todo: move this somehow to editdialog and add button to datagrid
  // can't figure out how to access List from Create-, EditModal (has to be possible)
  const EditModalButton = (props) => {
    const { source } = props
    const record = useRecordContext(props)
    return (
      <Button
        onClick={(e) => {
          edit(record.id)
          stateEditModal[1](true)
        }}
        label='ra.action.edit'
      >
        <EditIcon />
      </Button>
    )
  }

  const createComment = useCallback(
    async (values) => {
      const [relName, relId] = relation
      const pageId = parseInt(relId)
      // A comment belongs to a customer (relation) and can be about one of
      // their boats (linked_type/linked_id). A boat's page lists the comments
      // about it (listLinked): one written there is about this boat, filed
      // under the owner picked in the form, or under the boat when none is.
      const data = listLinked
        ? {
            ...values,
            relation: values.relation_id ? linkedType : relName,
            relation_id: values.relation_id ?? pageId,
            linked_type: relName,
            linked_id: pageId,
          }
        : { ...values, relation: relName, relation_id: pageId }
      await mutate(
        {
          type: 'create',
          resource: 'comments',
          payload: { data: { ...data, id: undefined } },
        },
        { returnPromise: true }
      )
      refresh()
      stateCreate[1](false)
    },
    [mutate, refresh, stateCreate, relation, listLinked, linkedType]
  )

  const editComment = useCallback(
    async (values) => {
      await mutate(
        {
          type: 'update',
          resource: 'comments',
          payload: {
            id: values.id,
            // Where the comment belongs stays as it is: this used to set it to
            // the page it was edited on, so editing a customer's comment from
            // a boat's page moved it off the customer.
            data: { ...values, updated_at: new Date() },
          },
        },
        { returnPromise: true }
      )
      refresh()
      stateEditModal[1](false)
    },
    [mutate, refresh, stateEditModal, relation]
  )

  const refCreateCanvas = useRef(null)

  const AttachmentField = (props) => {
    const { edit, ...rest } = props
    const record = useRecordContext(props)

    return record.rawFile instanceof File ? (
      <>
        <FileImageField
          record={record}
          {...rest}
          captionSource={edit === 'caption' ? null : 'caption'}
        />
        {edit && (
          <FormDataConsumer>
            {({ formData }) => {
              const index = formData.pictures.findIndex((p) => p.src === record.src)
              return (
                edit && (
                  <TextInput
                    source={`pictures[${index}].${edit}`}
                    label={`resources.attachments.fields.${edit}`}
                  />
                )
              )
            }}
          </FormDataConsumer>
        )}
      </>
    ) : (
      <>
        <FileImageField
          record={record}
          {...rest}
          source='uri'
          typeSource='content_type'
          captionSource={edit === 'caption' ? null : 'caption'}
        />
        {edit && (
          <FormDataConsumer>
            {({ formData }) => {
              const index = formData.pictures.findIndex((p) => p.id === record.id)
              return (
                edit && (
                  <TextInput
                    source={`pictures[${index}].${edit}`}
                    label={`resources.attachments.fields.${edit}`}
                  />
                )
              )
            }}
          </FormDataConsumer>
        )}
      </>
    )
  }

  const ExpandComment = (props) => {
    const { ...rest } = props
    const record = useRecordContext(props)

    return (
      <div>
        <RichTextField record={record} source='comment' />
        {record.pictures.map((picture) => (
          <AttachmentField record={picture} key={picture.uri} />
        ))}
      </div>
    )
  }

  const CommentFormInputs = ({ linkedType, linkedSelectionFilter, linkedOptionText }) => {
    const form = useFormContext()
    // On the SelectInput: react-admin 5's ReferenceInput takes no onChange, so
    // linked_type was never set and a comment never reached the boat's page.
    const onLinkedIdChanged = useCallback(
      (event) => {
        const linked_id = event?.target ? event.target.value : event
        form.setValue(listLinked ? 'relation' : 'linked_type', linked_id ? linkedType : null)
      },
      [form, linkedType]
    )

    return (
      <>
        <Grid container fullWidth spacing={1}>
          <Grid size={{ md: 7 }}>
            <AutocompleteTextInput source='type' label='resources.comments.fields.type' />
          </Grid>
          <Grid size={{ md: 5 }}>
            {linkedType && (
              <ReferenceInput
                source={listLinked ? 'relation_id' : 'linked_id'}
                reference={linkedType}
                filter={linkedSelectionFilter}
              >
                <SelectInput
                  onChange={onLinkedIdChanged}
                  label={`resources.comments.linked_types.${linkedType}`}
                  optionText={linkedOptionText}
                />
              </ReferenceInput>
            )}
          </Grid>
        </Grid>
        <RichTextInput source='comment' label='' validate={[required()]} />
        <FileInput
          source='pictures'
          label=''
          accept='image/*'
          multiple={true}
          refCanvas={refCreateCanvas}
        >
          <AttachmentField source='src' title='title' edit='caption' />
        </FileInput>
      </>
    )
  }

  return (
    <>
      <Empty {...props} icon={NotesIcon} resource='comments' create={false}>
        <Datagrid
          expand={<ExpandComment />}
          rowClick='expand'
          bulkActionButtons={disableDelete ? false : undefined}
        >
          <TextField source='id' label='#' />
          <TextField source='type' label='resources.comments.fields.type' />
          {linkedType && (
            <ReferenceField
              source={listLinked ? 'relation_id' : 'linked_id'}
              reference={linkedType}
              link='show'
              label={`resources.comments.linked_types.${linkedType}`}
            >
              {linkedType === 'boats' ? (
                <FunctionField
                  source='name'
                  render={(record) => `${record.name} (${record.model})`}
                />
              ) : (
                <FunctionField source='firstname' render={(record) => customerName(record)} />
              )}
            </ReferenceField>
          )}
          <DateField source='created_at' label='resources.comments.fields.created_at' />
          <DateField source='updated_at' label='resources.comments.fields.updated_at' />
          <EditModalButton />
          {disableDelete !== true && <DeleteButton redirect={false} />}
        </Datagrid>
      </Empty>

      <CreateModal
        title='resources.comments.create'
        state={stateCreate}
        {...props}
        resource='comments'
      >
        <>
          <SimpleForm
            onSubmit={createComment}
            // toolbar={<TabToolbar {...props} />}
          >
            <CommentFormInputs
              linkedType={linkedType}
              linkedSelectionFilter={linkedSelectionFilter}
              linkedOptionText={linkedOptionText}
            />
          </SimpleForm>
        </>
      </CreateModal>

      <EditModal state={stateEditModal} {...props} resource='comments' id={editCommentId}>
        {/* todo: don't know why props (redirect) are not passed to standard toolbar.. */}
        <TabbedForm
          syncWithLocation={false}
          onSubmit={editComment}
          toolbar={<TabToolbar {...props} />}
        >
          <FormTab label='ra.action.edit'>
            <CommentFormInputs
              linkedType={linkedType}
              linkedSelectionFilter={linkedSelectionFilter}
              linkedOptionText={linkedOptionText}
            />
          </FormTab>
          <FormTab label='resources.history.title'>
            <ReferenceManyField
              reference='history'
              target='relation_id'
              label=''
              filter={{ relation: 'comments' }}
              sort={{ field: 'time', order: 'DESC' }}
            >
              <HistoryDatagrid hideRelation />
            </ReferenceManyField>
          </FormTab>
        </TabbedForm>
      </EditModal>
    </>
  )
}

export default EditComments
